// Probes a video file's intrinsic dimensions by loading it into a transient
// `<video>` element. Used to reject portrait-oriented videos from
// landscape-only upload surfaces (hero/backdrop videos).
const getVideoDimensions = async (
  file: File
): Promise<{ width: number; height: number }> => {
  const video = document.createElement('video');
  const url = URL.createObjectURL(file);

  try {
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Could not load video metadata'));
    });
    return { width: video.videoWidth, height: video.videoHeight };
  } finally {
    URL.revokeObjectURL(url);
    video.remove();
  }
};

const isVerticalVideo = (width: number, height: number): boolean =>
  height > width;

export type FilterVerticalVideosResult = {
  accepted: File[];
  skippedCount: number;
};

// Returns only horizontal videos plus the count of vertical ones skipped.
// Non-video files pass through untouched. Callers decide how to surface the
// rejection — this helper is UI-agnostic.
export const filterVerticalVideos = async (
  files: File[]
): Promise<FilterVerticalVideosResult> => {
  const accepted: File[] = [];
  let skippedCount = 0;

  for (const file of files) {
    if (!file.type.startsWith('video/')) {
      accepted.push(file);
      continue;
    }

    const { width, height } = await getVideoDimensions(file);

    if (isVerticalVideo(width, height)) {
      skippedCount += 1;
    } else {
      accepted.push(file);
    }
  }

  return { accepted, skippedCount };
};
